/**
 * @file SortTable.jsx
 * @location src/components/team/SortTable.jsx
 * @role 정렬 가능한 평면 표 — 전체표(전체 컬럼) 정렬·렌더. 헤더 클릭으로 정렬축(기한/담당자/우선순위/상태) 토글.
 *   기본 정렬 기한 오름차순(기한 초과 상단). 새로 들어온 행은 useFlashNew 로 잠깐 강조.
 */
import { useMemo, useState } from 'react';
import { useMembers } from '../../store/useMembers';
import { getTodayStr } from '../../data/constants';
import { STATUS_ORDER, priorityMeta } from '../../data/labels';
import { TeamTaskRow, DEFAULT_COLS } from './TeamTaskRow';
import { useFlashNew } from './useFlashNew';

export function SortTable({ tasks, handleToggle, onToast, cols = DEFAULT_COLS }) {
  const { mname } = useMembers();
  const today = getTodayStr();
  const flashIds = useFlashNew(tasks);
  const [sort, setSort] = useState({ col: 'due', dir: 'asc' });

  const toggleSort = (col) =>
    setSort(prev => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );

  const sorted = useMemo(() => {
    return [...tasks].sort((a, b) => {
      let cmp = 0;
      if (sort.col === 'priority')    cmp = priorityMeta(a.priority).order - priorityMeta(b.priority).order;
      else if (sort.col === 'status') cmp = (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0);
      else if (sort.col === 'aee')    cmp = mname(a.aee).localeCompare(mname(b.aee), 'ja');
      else /* due */                  cmp = (a.due || today).localeCompare(b.due || today); // 무날짜(오늘)는 오늘로 취급
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [tasks, sort, mname]);

  const arrow = (col) => (sort.col === col ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');
  const th = (col, label) => {
    const ariaSort = sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        aria-sort={ariaSort}
        onClick={() => toggleSort(col)}>{label}{arrow(col)}</th>
    );
  };

  return (
    <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="task-table compact">
        <thead><tr>
          {cols.includes('check') && <th className="col-check">완료</th>}
          {cols.includes('status') && th('status', '상태')}
          {cols.includes('title') && <th className="col-title">업무</th>}
          {cols.includes('aee') && th('aee', '담당자')}
          {cols.includes('priority') && th('priority', '우선순위')}
          {cols.includes('due') && th('due', '일정')}
        </tr></thead>
        <tbody>
          {sorted.map(t => (
            <TeamTaskRow
              key={t.id} task={t} today={today} isNew={flashIds.has(t.id)} cols={cols}
              handleToggle={handleToggle}
              onToast={onToast}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
